import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useFormState } from 'react-hook-form';
import RMSCommonDialog from 'features/shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from 'features/shared/components/dialog';
import RMSRecordHistoryButton from 'features/shared/components/modules/RMSRecordHistoryButton';
import { executeApiAction } from 'features/shared/utility/apiHelper';
import useConfirmDialog from 'features/shared/utility/useConfirmDialog';
import { notify } from 'reducers/NotifierReducer';
import { normalizeEmptyToNull } from 'features/shared/utility/helper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import { RMSMenuSidebar } from 'features/shared/components/sidebars';
import {
  VehicleSidebarGroups,
  getSelectedVehicleMenu,
} from 'features/user/constants/vehicleSidebarMenuHelper';
import {
  hideEditVehicleDialog,
  vehicleDialogUpdated,
} from 'features/user/store/actions/editVehicleDialog.actions';
import {
  EDIT_VEHICLE_DEFAULT_VALUES,
  VEHICLE_TAB_KEYS,
} from '../constants/addVehicleFormConstants';
import { getVehicleDetails, updateVehicleDetails } from '../services/vehicleService';
import CreateFormResolver from 'features/shared/utility/createFormResolver';
import editVehicleSchema from '../schema/editVehicleSchema';

const EditVehicleDialog = ({ dialog }) => {
  const dispatch = useDispatch();
  const { vehicleId: ptsVehicleID, requestId } = dialog;
  const onClose = useCallback(() => dispatch(hideEditVehicleDialog(requestId)), [
    dispatch,
    requestId,
  ]);
  const [activeTab, setActiveTab] = useState(VEHICLE_TAB_KEYS.EDIT);
  const ActiveContent = getSelectedVehicleMenu(activeTab)?.content;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [vehicle, setVehicle] = useState(null);

  const permissions = usePermissions(PERMISSION_RESOURCES.VEHICLE);
  const agencyId = useSelector((state) => state.user.userAgency);
  const propertyPermissions = usePermissions(PERMISSION_RESOURCES.PROPERTY, agencyId);
  const canSaveVehicle = Boolean(permissions.Edit && propertyPermissions.Edit);
  const isViewOnly = !canSaveVehicle;

  const methods = useForm({
    resolver: CreateFormResolver(editVehicleSchema),
    mode: 'onSubmit',
    defaultValues: EDIT_VEHICLE_DEFAULT_VALUES,
    disabled: isViewOnly,
  });

  const { control } = methods;

  const { isDirty } = useFormState({ control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const onSubmit = useCallback(
    async (data) => {
      if (!canSaveVehicle) return;
      const payload = {
        Year: normalizeEmptyToNull(data.Year),
        Make: data.Make || null,
        Model: data.Model || null,
        Style: data.Style || null,
        DoorQuantity: normalizeEmptyToNull(data.DoorQuantity),
        PassengerSafeQty: normalizeEmptyToNull(data.PassengerSafeQty),
        SeatQty: normalizeEmptyToNull(data.SeatQty),
        PrimaryColor: data.PrimaryColor || null,
        SecondaryColor: data.SecondaryColor || null,
        InteriorColor: data.InteriorColor || null,
        Classification: data.Classification || null,
        Description: data.Description || null,
        Brand: data.Brand || null,
        Brander: data.Brander || null,
        VIN: data.VIN || null,
        VINAnalysisText: data.VINAnalysisText || null,
        PropertyID: data.PropertyID || null,
        Property: data.Property || null,
        CommercialID: data.CommercialID || null,
        Commercial: data.Commercial || null,
        EmissionInspectionQty: normalizeEmptyToNull(data.EmissionInspectionQty),
        Transmission: data.Transmission || null,
        IsCMVIndicator: data.IsCMVIndicator || false,
        IsRented: data.IsRented || false,
        IsWanted: data.IsWanted || false,
        GarageIndicator: data.GarageIndicator || false,
        GarageFacility: data.GarageFacility || null,
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => updateVehicleDetails(ptsVehicleID, payload),
        onSuccess: () => {
          dispatch(notify('Vehicle updated successfully', 'success'));
          dispatch(vehicleDialogUpdated({ ...vehicle, ...payload, ptsVehicleID }, requestId));
          onClose?.();
        },
      });
    },
    [ptsVehicleID, dispatch, onClose, requestId, vehicle, canSaveVehicle]
  );

  const handleSave = useMemo(() => methods.handleSubmit(onSubmit), [methods, onSubmit]);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this vehicle. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  useEffect(() => {
    if (!ptsVehicleID) return;
    let active = true;

    executeApiAction({
      setLoading: (value) => {
        if (active) setLoading(value);
      },
      dispatch,
      action: () => getVehicleDetails(ptsVehicleID),
      onSuccess: (data) => {
        if (!active) return;
        setVehicle(data || null);
        methods.reset({
          Year: data?.Year ?? '',
          Make: data?.Make || '',
          Model: data?.Model || '',
          Style: data?.Style || '',
          DoorQuantity: data?.DoorQuantity ?? '',
          PassengerSafeQty: data?.PassengerSafeQty ?? '',
          SeatQty: data?.SeatQty ?? '',
          PrimaryColor: data?.PrimaryColor || '',
          SecondaryColor: data?.SecondaryColor || '',
          InteriorColor: data?.InteriorColor || '',
          Classification: data?.Classification || '',
          Description: data?.Description || '',
          Brand: data?.Brand || '',
          Brander: data?.Brander || '',
          VIN: data?.VIN || '',
          VINAnalysisText: data?.VINAnalysisText || '',
          PropertyID: data?.PropertyID || '',
          Property: data?.Property || '',
          CommercialID: data?.CommercialID || '',
          Commercial: data?.Commercial || '',
          EmissionInspectionQty: data?.EmissionInspectionQty ?? '',
          Transmission: data?.Transmission || '',
          IsCMVIndicator: Boolean(data?.IsCMVIndicator),
          IsRented: Boolean(data?.IsRented),
          IsWanted: Boolean(data?.IsWanted),
          GarageIndicator: Boolean(data?.GarageIndicator),
          GarageFacility: data?.GarageFacility || '',
        });
      },
    });
    return () => {
      active = false;
    };
  }, [ptsVehicleID, dispatch, methods]);

  return (
    <RMSCommonDialog
      open
      title={isViewOnly ? 'View Vehicle' : 'Edit Vehicle'}
      onClose={requestClose}
      onSave={handleSave}
      loading={loading || submitting}
      saveDisabled={submitting || !canSaveVehicle}
      fullScreen
      className="edit-vehicle-page-content"
      dialogClassName="edit-vehicle-dialog"
      maxWidth="lg"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: activeTab === VEHICLE_TAB_KEYS.EDIT,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        activeTab === VEHICLE_TAB_KEYS.EDIT && (
          <RMSRecordHistoryButton
            disabled={!vehicle?.Created}
            created={vehicle?.Created}
            createdBy={vehicle?.CreatedBy}
            updated={vehicle?.Updated}
            updatedBy={vehicle?.UpdatedBy}
          />
        )
      }>
      <div className="edit-vehicle-page">
        <RMSMenuSidebar
          groups={VehicleSidebarGroups}
          activeKey={activeTab}
          className="app-sidebar--bordered"
          disabled={loading || submitting}
          onSelect={(key) => {
            if (key === activeTab || loading || submitting) return;
            const changeMenu = () => {
              methods.reset();
              setActiveTab(key);
            };
            if (activeTab === VEHICLE_TAB_KEYS.EDIT && isDirty)
              requestConfirm(
                'You have unsaved vehicle changes. Leave without saving?',
                changeMenu,
                'Unsaved Changes'
              );
            else changeMenu();
          }}
        />
        <main
          className={`edit-vehicle-page__content${
            activeTab === VEHICLE_TAB_KEYS.EDIT ? '' : ' edit-vehicle-page__content--grid'
          }`}>
          {ActiveContent && (
            <FormProvider {...methods}>
              <ActiveContent ptsVehicleID={ptsVehicleID} canEdit={canSaveVehicle} />
            </FormProvider>
          )}
        </main>
      </div>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default EditVehicleDialog;
